import {
  Box, Flex, Image, Heading, Text, Button, Badge, Divider,
  List, ListItem, ListIcon, useToast,
} from '@chakra-ui/react'
import { CheckCircleIcon, DownloadIcon } from '@chakra-ui/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { GET_WORKSHEET_BY_ID } from 'store/worksheets'
import api from 'store/api'
import Cookies from 'js-cookie'

import eng from 'assets/img/5.png'
import mathPost from 'assets/img/1.png'
import mathPre from 'assets/img/2.png'
import sciPost from 'assets/img/3.png'
import sciPre from 'assets/img/4.png'
import social from 'assets/img/6.png'
import thai from 'assets/img/7.png'

const imageSubject = (subject, classNum) => {
  const num = classNum && classNum.split(' ')[1]
  if (subject === 'คณิตศาสตร์' && num < 4) return mathPre
  if (subject === 'คณิตศาสตร์' && num >= 4) return mathPost
  if (subject === 'วิทยาศาสตร์' && num < 4) return sciPre
  if (subject === 'วิทยาศาสตร์' && num >= 4) return sciPost
  if (subject === 'สังคมศึกษา') return social
  if (subject === 'ภาษาอังกฤษ') return eng
  if (subject === 'ภาษาไทย') return thai
}

export default function WorksheetDetail() {
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const toast = useToast()
  const [downloading, setDownloading] = useState(false)

  const initFetch = useCallback(() => {
    dispatch(GET_WORKSHEET_BY_ID(id))
  }, [dispatch, id])

  useEffect(() => {
    initFetch()
    window.scrollTo(0, 0)
  }, [initFetch])

  const worksheet = useSelector((state) => state.worksheets.worksheet)

  const handleDownload = async () => {
    const token = Cookies.get('token')
    if (!token) {
      history.push('/auth/login')
      return
    }

    setDownloading(true)
    try {
      const response = await api.downloadWorksheet(id)
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${worksheet.name || 'worksheet'}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const status = err.response?.status
      if (status === 401) {
        history.push('/auth/login')
      } else if (status === 403) {
        toast({
          title: 'ไม่มีสิทธิ์เข้าถึง',
          description: 'คุณยังไม่ได้รับสิทธิ์ในการดาวน์โหลดใบงานนี้ กรุณาติดต่อผู้ดูแลระบบ',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'เกิดข้อผิดพลาด',
          description: 'ไม่สามารถดาวน์โหลดได้ในขณะนี้',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      }
    } finally {
      setDownloading(false)
    }
  }

  if (!worksheet) return null

  return (
    <Box bg='gray.50' minH='100vh' p={4}>
      <Flex maxW='1200px' mx='auto' direction={{ base: 'column', lg: 'row' }}>
        {/* Left Content */}
        <Box flex='2' pr={{ base: 0, lg: 6 }}>
          {worksheet.pdf_file && (
            <Box mb={6} border='1px solid' borderColor='gray.200' borderRadius='md' overflow='hidden'>
              <Box bg='gray.50' px={4} py={2} borderBottom='1px solid' borderColor='gray.200'>
                <Text fontSize='sm' fontWeight='bold' color='gray.600'>ตัวอย่างใบงาน</Text>
              </Box>
              <Box position='relative'>
                <iframe
                  src={`${process.env.REACT_APP_API_PATH}/api/worksheet/${id}/preview#toolbar=0&navpanes=0&scrollbar=0`}
                  width='100%'
                  height='520px'
                  style={{ border: 'none', display: 'block' }}
                  title='ตัวอย่างใบงาน'
                />
              </Box>
            </Box>
          )}
          {!worksheet.pdf_file && (
            <Box mb={6}>
              <Image
                src={worksheet.url_image || imageSubject(worksheet.subject, worksheet.class)}
                alt={worksheet.name}
                w='full'
                objectFit='cover'
                borderRadius='md'
              />
            </Box>
          )}
          <Heading as='h1' size='lg' mb={2}>{worksheet.name}</Heading>
          <Flex align='center' mb={4} gap={2} flexWrap='wrap'>
            <Badge colorScheme='teal'>{worksheet.class}</Badge>
            <Badge colorScheme='teal'>{worksheet.subject}</Badge>
            {worksheet.is_new && <Badge colorScheme='blue'>ใหม่</Badge>}
            {worksheet.is_recommended && <Badge colorScheme='purple'>แนะนำ</Badge>}
            {worksheet.is_bestseller && <Badge colorScheme='orange'>ขายดี</Badge>}
          </Flex>
          {worksheet.description && (
            <Box>
              <Heading as='h2' size='sm' mb={2}>รายละเอียด:</Heading>
              <List spacing={2}>
                {worksheet.description.split('\n').map((item, i) => (
                  <ListItem key={i} pl={4}>
                    <ListIcon as={CheckCircleIcon} color='teal.500' />
                    {item.replace(/^-\s*/, '')}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        {/* Right Sidebar */}
        <Box flex='1' bg='white' p={6} boxShadow='lg' borderRadius='md' maxH='fit-content'>
          {worksheet.is_free ? (
            <Text fontWeight='bold' fontSize='2xl' color='green.500' mb={4}>ฟรี</Text>
          ) : worksheet.sale_price ? (
            <>
              <Text fontWeight='bold' fontSize='2xl' color='teal.600'>฿{worksheet.sale_price}.00 บาท</Text>
              <Text as='s' color='gray.500' fontSize='sm' mb={4} display='block'>
                ฿{worksheet.original_price}.00 บาท
              </Text>
            </>
          ) : (
            <Text fontWeight='bold' fontSize='2xl' color='teal.600' mb={4}>
              ฿{worksheet.original_price}.00 บาท
            </Text>
          )}

          {!worksheet.is_free && (
            <a href='https://line.me/R/ti/p/%40838jyolz' target='_blank' rel='noreferrer'>
              <Button colorScheme='teal' size='lg' w='full' mb={4}>
                ซื้อใบงานผ่าน Line
              </Button>
            </a>
          )}

          {/* <Button
            colorScheme='teal'
            variant={worksheet.is_free ? 'solid' : 'outline'}
            size='lg'
            w='full'
            mb={4}
            leftIcon={<DownloadIcon />}
            onClick={handleDownload}
            isLoading={downloading}
            loadingText='กำลังดาวน์โหลด...'
          >
            ดาวน์โหลด PDF
          </Button> */}

          <Divider mb={4} />

          <Box>
            <Heading as='h2' size='sm' mb={2}>สิ่งที่คุณจะได้รับ:</Heading>
            <List spacing={2}>
              <ListItem pl={4}>
                <ListIcon as={CheckCircleIcon} color='teal.500' />
                ใบงาน PDF คุณภาพสูง
              </ListItem>
              {worksheet.description && worksheet.description.split('\n').map((item, i) => (
                <ListItem key={i} pl={4}>
                  <ListIcon as={CheckCircleIcon} color='teal.500' />
                  {item.replace(/^-\s*/, '')}
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
